import { trackUiError } from "../utils/TrackingUtil";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import type { Country, ServiceError, StoreId, UserId } from "../stores/BaseTypes";
import type {
	CheckoutData,
	InitialEmployeeCheckoutHeader,
	CreditCardData,
	OrderSource,
	ServiceTypeSelection,
	PaymentMethodId,
	ReducedCreditCardData,
} from "./CheckoutData";
import type { FulfillmentType } from "./ArticleTypes";
import isEmpty from "../utils/isEmpty";
export type CheckoutUserData = {
	country: Country;
	storeId: StoreId;
	customerId: UserId;
	additionalId: string;
};
export type CreateCheckoutRequest = {
	userData: CheckoutUserData;
	cartId?: string;
	cartVersion?: number;
	creationContext: {
		preselectedDeliveryAddressId: string | null | undefined;
		preselectedPickupStoreId: string | null | undefined;
		initialEmployeeCheckoutHeader?: InitialEmployeeCheckoutHeader | null;
		checkoutId?: string;
	};
	locale?: string;
};
export type LoadCheckoutRequest = CheckoutUserData;
export type SubmitEvent = {
	creditCardData?: CreditCardData | null;
	pspOrderId?: string | null;
	saveCreditCardPermitted?: boolean | null;
	useNewPaymentSelection?: boolean | null;
};
export type SubmitCheckoutRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	submitEvent: SubmitEvent;
	locale?: string;
};
export type UpdateHeaderRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	event: UpdateHeaderEvent;
};
export type SelectAddressRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	addressSelection: {
		addressId: string;
	};
};
export type SetDeliveryPhoneNumberRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	deliveryPhoneNumberSelection: {
		deliveryPhoneNumber: string | null | undefined;
	};
};
export type SetEmailAddressRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	emailAddressSelection: {
		emailAddress: string | null | undefined;
	};
};
export type SelectPickupStoreRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	pickupStoreSelection: {
		storeId: string;
	};
};
export type RemoveItemsRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	items: string[];
};
export type PutPaymentMethodSelectionRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	event: RemotePaymentSelectedEvent;
};
export type UpdateServiceTypeSelectionRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	serviceTypeSelection: ServiceTypeSelection;
};
export type PaymentSelectedEvent = {
	mainPaymentId: PaymentMethodId;
	mainPaymentFulfillmentTypes: FulfillmentType[];
	complementaryPaymentId: PaymentMethodId | null | undefined;
	creditCard: ReducedCreditCardData | null | undefined;
};
export type RemotePaymentSelectedEvent = {
	mainPaymentId: PaymentMethodId;
	mainPaymentFulfillmentTypes: FulfillmentType[];
	complementaryPaymentId: PaymentMethodId | null | undefined;
	creditCardToken: string | null | undefined;
};
export type VoucherRequest = {
	userData: CheckoutUserData;
	checkoutId: string;
	code: string;
};
export type UpdateHeaderCommentEvent = {
	eventType: "CommentSetEvent";
	value: string;
};
export type UpdateHeaderOrderSourceEvent = {
	eventType: "OrderSourceSetEvent";
	value: OrderSource;
};
export type UpdateHeaderOwnOrderNumberEvent = {
	eventType: "OwnOrderNumberSetEvent";
	value: string;
};
export type UpdateHeaderOwnStoreNumberEvent = {
	eventType: "OwnStoreNumberSetEvent";
	value: string;
};
export type UpdateHeaderEgalTaxAppliedEvent = {
	eventType: "EgalTaxAppliedSetEvent";
	value: boolean;
};
export type UpdateHeaderIgnoreFeesEvent = {
	eventType: "IgnoreFeesSetEvent";
	value: boolean;
};
export type UpdateHeaderSendPickingListToCentralWarehouseEvent = {
	eventType: "ExternalCommissioningSetEvent";
	value: boolean;
};
export type UpdateHeaderSendOrderConfirmationToCardholderEvent = {
	eventType: "ConfirmOrderToCardholderSetEvent";
	value: boolean;
};
export type UpdateHeaderSendOrderConfirmationToCustomerEvent = {
	eventType: "ConfirmOrderToCustomerSetEvent";
	value: boolean;
};
export type UpdateHeaderSingleInvoiceEvent = {
	eventType: "SingleInvoiceSetEvent";
	value: boolean;
};
export type UpdateHeaderPickingCommentAdditionEvent = {
	eventType: "PickingCommentSetEvent";
	value: string;
};
export type UpdateHeaderDeliveryCommentAdditionEvent = {
	eventType: "DeliveryCommentSetEvent";
	value: string;
};
export type UpdateHeaderInvoiceCommentAdditionEvent = {
	eventType: "InvoiceCommentSetEvent";
	value: string;
};
export type UpdateHeaderDeliveryDateEvent = {
	eventType: "DeliveryDateSetEvent";
	value: string;
};
export type UpdateHeaderPickupDateEvent = {
	eventType: "PickupDateSetEvent";
	value: string;
};
export type UpdateHeaderFiscalReceiptEvent = {
	eventType: "FiscalReceiptSetEvent";
	value: boolean;
};
export type UpdatePublicOfficeContractNumber = {
	eventType: "PublicOfficeContractNumberSetEvent";
	value: string;
};
export type UpdatePublicOfficeProjectNumber = {
	eventType: "PublicOfficeProjectNumberSetEvent";
	value: string;
};
export type UpdateRedeemLoyaltyPoints = {
	eventType: "RedeemLoyaltyPointsSetEvent";
	value: string;
};
export type UpdateHeaderEvent =
	| UpdateHeaderCommentEvent
	| UpdateHeaderOrderSourceEvent
	| UpdateHeaderOwnOrderNumberEvent
	| UpdateHeaderOwnStoreNumberEvent
	| UpdateHeaderEgalTaxAppliedEvent
	| UpdateHeaderIgnoreFeesEvent
	| UpdateHeaderSendPickingListToCentralWarehouseEvent
	| UpdateHeaderSendOrderConfirmationToCardholderEvent
	| UpdateHeaderSendOrderConfirmationToCustomerEvent
	| UpdateHeaderSingleInvoiceEvent
	| UpdateHeaderPickingCommentAdditionEvent
	| UpdateHeaderDeliveryCommentAdditionEvent
	| UpdateHeaderInvoiceCommentAdditionEvent
	| UpdateHeaderFiscalReceiptEvent
	| UpdateHeaderDeliveryDateEvent
	| UpdateHeaderPickupDateEvent
	| UpdatePublicOfficeContractNumber
	| UpdatePublicOfficeProjectNumber
	| UpdateRedeemLoyaltyPoints;

function validateUserData(userData: CheckoutUserData, trackError = true): CheckoutUserData | null | undefined {
	const errorMessages = {
		country: "Missing country",
		customerId: "Missing customer ID",
		storeId: "Missing store ID",
		additionalId: "Missing employee ID or cardholder number",
	};

	for (const key in errorMessages) {
		if (isEmpty(userData[key])) {
			if (trackError) {
				trackUiError("validateUserData", errorMessages[key]);
			}

			return null;
		}
	}

	return userData;
}

export function createCheckoutRequest(
	userData: CheckoutUserData,
	initialEmployeeCheckoutHeader: InitialEmployeeCheckoutHeader | null | undefined,
	cartId?: string,
	cartVersion?: number,
): CreateCheckoutRequest | null | undefined {
	if (!userData) {
		return null;
	}

	const preselectedDeliveryAddressId = SessionStore.getSelectedDeliveryAddressHash();

	if (!/^\d+-/.test(preselectedDeliveryAddressId)) {
		trackUiError(
			"createCheckoutRequest",
			`Read fishy delivery address id from SessionStore: "${preselectedDeliveryAddressId}".
        State in SessionStore: ${JSON.stringify(SessionStore.getSelectedDeliveryAddress())}`,
			new Error(),
		);
	}

	return {
		userData,
		cartId: cartId,
		cartVersion: cartVersion,
		creationContext: {
			initialEmployeeCheckoutHeader,
			preselectedDeliveryAddressId,
			preselectedPickupStoreId: SessionStore.getCustomerStore(),
		},
	};
}
export function buildUserData(checkout: CheckoutData | null | undefined): CheckoutUserData | null | undefined {
	if (!checkout) {
		trackUiError("buildUserData", "Missing checkout");
		return null;
	}

	if (!checkout.context) {
		trackUiError("buildUserData", "Missing checkout context");
		return null;
	}

	const userData: CheckoutUserData = {
		country: checkout.context.country,
		storeId: checkout.context.storeId,
		customerId: checkout.context.customerId,
		additionalId: checkout.context.employeeId || checkout.context.cardholderId || "",
	};
	return validateUserData(userData);
}
export function updateHeaderRequest(
	checkout: CheckoutData | null | undefined,
	event: UpdateHeaderEvent,
): UpdateHeaderRequest | null | undefined {
	const userData = buildUserData(checkout);

	if (!checkout || !userData) {
		return null;
	}

	const checkoutId = checkout.id;
	return {
		userData,
		checkoutId,
		event,
	};
}
export function putPaymentMethodSelectionRequest(
	checkout: CheckoutData | null | undefined,
	event: PaymentSelectedEvent,
): PutPaymentMethodSelectionRequest | null | undefined {
	const userData = buildUserData(checkout);

	if (!checkout || !userData) {
		return null;
	}

	const checkoutId = checkout.id;
	const { creditCard, ...restEventParams } = event;
	const remoteEvent = {
		creditCardToken: (creditCard || {}).cardToken,
		...restEventParams,
	};
	return {
		userData,
		checkoutId,
		event: remoteEvent,
	};
}
export function retryingErrorHandler(
	retryCallback: () => void,
	failureCallback: (arg0: ServiceError) => void,
): (arg0: ServiceError) => void {
	return (error: ServiceError) => {
		if (error.code === "OC-409") {
			// wrong version -> retryable
			retryCallback();
		} else {
			// not retryable call failure callback
			failureCallback(error);
		}
	};
}
